import type { IRoute, SidebarGroups } from 'v2/types'
import { type RFeatureFlags, UserRole } from 'v2/types/api/auth'
import type { RViews } from 'v2/types/api/conversations'
import type { RUserProfile } from 'v2/types/api/settings'
import type { ModulePermissionTypeMap } from 'v2/types/rbac'
import { getSessionRoles } from 'v2/utils'
import { getAiStudioRoutes } from './ai-studio'
import { openRoutes } from './auth'
import { getConversationsRoutes } from './conversations'
import { getMainRoutes } from './main'
import { getOtherRoutes } from './other'
import { getSettingsRoutes } from './settings'

/**
 * Array of route objects representing the application routes.
 * Each route object contains information such as title, path, icon, and component.
 */
export const protectedRoutes: (meta?: {
  featureFlags?: RFeatureFlags
  roles?: UserRole[]
  views?: RViews
  unassignedCount?: number
  location?: string
  permissionsMap?: ModulePermissionTypeMap
  profileData?: RUserProfile // Full profile data instead of just activeSeats
  isIndianRegion?: boolean
  isUsRegion?: boolean
  accountProductType?: string
  hasSenderIdAccess?: boolean
  isVerifyLocked?: boolean
  /** False in the India region, where Verify is not offered at all. */
  isVerifyAvailable?: boolean
  isWhatsappLocked?: boolean
}) => IRoute[] = (meta) => {
  return [
    ...getAiStudioRoutes(meta),
    ...getConversationsRoutes(meta),
    ...getSettingsRoutes(meta),
    ...getOtherRoutes(meta),
    ...getMainRoutes(meta),
  ]
}

export { openRoutes }

export const getDefaultRoute = () => {
  const roles = getSessionRoles()
  const isOnlyAgent = roles.length === 1 && roles[0] === UserRole.agent
  return isOnlyAgent ? '/conversations/active/c' : '/home'
}

interface RouteMap {
  [key: string]: string
}

const formatSidebarGroupName = (group: SidebarGroups): string => {
  return group.charAt(0).toUpperCase() + group.slice(1)
}

function getAllRouteTitles(
  routes: IRoute[],
  parentPath = '',
  parentSidebarGroup?: SidebarGroups,
): RouteMap {
  return routes
    .flatMap((route) => {
      const entries: [string, string][] = []

      // Handle path concatenation
      let currentPath = route.path || ''
      if (!currentPath.startsWith('http')) {
        currentPath = currentPath.startsWith('/') ? currentPath : `${parentPath}/${currentPath}`
        currentPath = currentPath.replace(/\/+/g, '/')
      }

      const sidebarGroup = route.sidebar?.group || parentSidebarGroup

      if (route.title && route.path) {
        let title = typeof route.title === 'string' ? route.title : 'Console'

        if (title === 'Applications') {
          title = 'Apps'
        }

        // Add group prefix for grouped routes (e.g., "Build - Agents")
        if (sidebarGroup && !currentPath.startsWith('/api')) {
          const groupName = formatSidebarGroupName(sidebarGroup)
          title = `${groupName} - ${title}`
        }

        entries.push([currentPath, title])

        if (currentPath.includes('/:')) {
          const pattern = currentPath.replace(/:\w+/g, '[^/]+')
          entries.push([`__pattern__${pattern}`, title])
        }
      }

      if (route.children?.length) {
        const groupForChildren = route.sidebar?.group || parentSidebarGroup
        const childEntries = getAllRouteTitles(route.children, currentPath, groupForChildren)
        entries.push(...Object.entries(childEntries))
      }

      return entries
    })
    .reduce((acc, [path, title]) => {
      acc[path] = title
      return acc
    }, {} as RouteMap)
}

// Generate routeTitleMap with API Console routes included for title lookup only
// NOTE: This does NOT affect actual route access/permissions - those are controlled by useRoutes() hook
// which uses real feature flags. This map is only used for document title display.
// Built lazily: building this at module scope instantiates every route element
// (getAiStudioRoutes -> <AgentBuilder />) and throws a TDZ ReferenceError whenever this
// module is first reached from inside the agent-builder -> 404 -> routes import cycle.
let routeTitleMapCache: RouteMap | undefined
const getRouteTitleMap = (): RouteMap => {
  if (!routeTitleMapCache) {
    routeTitleMapCache = getAllRouteTitles([
      ...protectedRoutes({
        location: '/api',
        featureFlags: {} as Partial<RFeatureFlags> as RFeatureFlags,
      }),
      ...openRoutes,
    ])
  }
  return routeTitleMapCache
}

// Helper function to get title for a path
// Used to set the document title
export function getRouteTitle(path: string): string | undefined {
  // Custom rule for conversation routes

  if (path.startsWith('/conversations')) return 'Conversations'
  if (path.startsWith('/agent/legacy')) return 'Agent Builder'

  const routeTitleMap = getRouteTitleMap()

  // First try exact match
  if (routeTitleMap[path]) return routeTitleMap[path]

  // If no exact match, try matching against patterns
  const matchedPattern = Object.keys(routeTitleMap)
    .filter((key) => key.startsWith('__pattern__'))
    .map((key) => key.replace('__pattern__', ''))
    .find((pattern) => new RegExp(`^${pattern}$`).test(path))

  return matchedPattern ? routeTitleMap[`__pattern__${matchedPattern}`] : undefined
}
